import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons/faCircleInfo';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons/faTriangleExclamation';
import { timingCounts } from 'fym-shared/src/music24/lyricTiming';
import { CatalogItemKind } from 'fym-shared/src/smartlinks/catalog';
import { useDeleteCatalogItemMutation, useGetLyricTimingQuery } from '../../../../../redux/api/fymAPI';
import { CatalogItem, CatalogSong, ReleaseKitSummary } from '../../../../../redux/api/fymAPI.types';
import { useShowToast } from '../../../../../redux/slices/toasts/hooks';
import { useSMLRouter } from '../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../routing/sml/SMLRoutes';
import { TypedMemo } from '../../../../common/types';
import { LoadingView } from '../../../../common/views/LoadingView';
import { ErrorView } from '../../../../common/views/ErrorView/ErrorView';
import { errorAlertMessage } from '../../../../common/views/ErrorAlert/errorAlertMessage';
import { readM24Error } from '../../../../music24/utils/parseM24LimitError';
import { useOpenReleasePage } from '../../../../music24/promote/releases/useOpenReleasePage';
import { RELEASE_FOCUS_AI_REEL } from '../../../../music24/promote/releases/releasePageLink';
import { SMLCreationSheet } from '../../../creationSheet/SMLCreationSheet';
import { useOpenPresaveWizardForSong } from '../../../wizards/presaveLink/useOpenPresaveWizardForSong';
import { Breadcrumbs } from '../../../smlBreadcrumbs/types';
import { catalogCreationSource } from '../../catalogCreationSource';
import { SongMadeList } from '../SongMadeList';
import { catalogSongPath, SongLyricsView, SongPageSection } from './catalogSongPath';
import { useSongPageDraft } from './draft/useSongPageDraft';
import { SongPageHeader } from './SongPageHeader';
import { MergeSongsModal } from '../../merge/MergeSongsModal';
import { scrollToSongSection, SongPageBar } from './SongPageBar';
import { SongPageSectionBlock } from './SongPageSectionBlock';
import { SongDetailsSection } from './details/SongDetailsSection';
import { SongFilesSection } from './files/SongFilesSection';
import { SongLyricsSection, SongLyricsViewSwitch, SONG_LYRICS_INPUT_ID } from './lyrics/SongLyricsSection';
import { SongSmartLinkCard } from './release/SongSmartLinkCard';
import { ServicesPanel } from '../../../../music24/servicesPanel/ServicesPanel';
import { SongKitCard } from './release/SongKitCard';
import { SongRemoveZone } from './release/SongRemoveZone';
import { SongRemoveDialog } from './release/SongRemoveDialog';
import { changedFieldNames, SongSaveBar } from './SongSaveBar';
import { SongLeaveDialog } from './SongLeaveDialog';
import { useLeaveGuard } from './useLeaveGuard';
import { songPageMessages } from './songPageMessages';
import { Panel, PanelBody } from '../../../../music24/components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the song page; each part uses its own slice of it */
import styles from './SongPage.module.scss';
import { trackEvent } from '../../../../analytics/sml/analytics';
/* eslint-enable css-modules/no-unused-class */

export interface SongPageRole {
  /** The demo artist: nothing changes (design f7). */
  readOnly: boolean;
  /** `MEMBER` edits the song but does not remove one with a master (owner decision 3). */
  role: 'OWNER' | 'MEMBER';
  ownerEmail: string | null;
  workspaceName: string | null;
  /** The member note shows once per workspace (f6); the page stores the dismissal. */
  showMemberNote: boolean;
  onDismissMemberNote: () => void;
}

interface SongPageProps extends SongPageRole {
  accountId: number;
  item: CatalogItem;
  song: CatalogSong | undefined;
  songError: unknown;
  retrySong: () => void;
  kit: ReleaseKitSummary | undefined;
  breadcrumbs: Breadcrumbs;
  /** `?tab=`: the section to scroll to once the page is there. */
  initialSection: SongPageSection | undefined;
  initialView: SongLyricsView | undefined;
}

/**
 * The song page of one catalog row (design song-page; owner decisions 2026-10-06): one long page
 * with Details, Song files, Lyrics and Links and release, one save bar for the text fields, and a
 * leave guard for unsaved changes and a running upload.
 */
const SongPageComponent: React.FC<SongPageProps> = ({
  accountId,
  item,
  song,
  songError,
  retrySong,
  kit,
  breadcrumbs,
  initialSection,
  initialView,
  readOnly,
  role,
  ownerEmail,
  workspaceName,
  showMemberNote,
  onDismissMemberNote,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const router = useSMLRouter();
  const { showSuccess } = useShowToast();
  const form = useSongPageDraft({ accountId, item, song, readOnly });
  const [isUploading, setIsUploading] = useState(false);
  const guard = useLeaveGuard(!readOnly && (form.isDirty || isUploading));
  const hasLyrics = song !== undefined && song.lyrics !== null && song.lyrics.trim() !== '';
  const [view, setView] = useState<SongLyricsView | undefined>(initialView);
  const lyricsView: SongLyricsView = view ?? (song !== undefined && !hasLyrics ? 'text' : 'times');
  const [isRemoveOpen, setIsRemoveOpen] = useState(false);
  const [isOwnerOnlyForced, setIsOwnerOnlyForced] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  // design catalog-merge a1: "Merge with another song…" of the head menu.
  const [isMergeOpen, setIsMergeOpen] = useState(false);
  const openMerge = useCallback(() => setIsMergeOpen(true), []);
  const closeMerge = useCallback(() => setIsMergeOpen(false), []);
  const [deleteItem, deletion] = useDeleteCatalogItemMutation();
  const isClaimed = item.kind !== CatalogItemKind.unreleased;
  const hasMaster = (song?.master ?? null) !== null || item.song.hasMaster;
  const isOwnerOnly = !readOnly && (isOwnerOnlyForced || (role === 'MEMBER' && hasMaster));

  // The hand-set times: Replace and Re-sync say that they go (c6, d8). The lines come with the
  // lyric timing, which the Lyrics section reads too (one cache entry).
  const timing = useGetLyricTimingQuery(
    { accountId, itemUuid: item.uuid },
    { skip: song === undefined || song.lyricTiming === null || song.lyricTiming === undefined }
  );
  const editedCount = useMemo(
    () => (timing.data?.timing ? timingCounts(timing.data.timing).editedCount : (song?.lyricTiming?.editedCount ?? 0)),
    [song?.lyricTiming?.editedCount, timing.data]
  );

  // `?tab=` scrolls one time, when the sections are on the page (owner decision 2: old deep links).
  const scrolled = useRef(false);
  useEffect(() => {
    if (scrolled.current || initialSection === undefined || song === undefined) {
      return;
    }
    scrolled.current = true;
    scrollToSongSection(initialSection);
  }, [initialSection, song]);

  const openStudio = useCallback(
    () => router.push(SMLRoutes.promoteStudio, { catalogItemUuid: item.uuid }),
    [item.uuid, router]
  );
  const openReleasePage = useOpenReleasePage({ accountId });
  const openReleases = useCallback(() => openReleasePage(item.uuid), [item.uuid, openReleasePage]);
  const openReels = useCallback(() => openReleasePage(item.uuid, RELEASE_FOCUS_AI_REEL), [item.uuid, openReleasePage]);
  const openCalendar = useCallback(() => router.push(SMLRoutes.promoteCalendar), [router]);
  const openSmartLink = useCallback(
    (smartLinkId: number) => router.push(SMLRoutes.manage, { accountId, smartLinkId }),
    [accountId, router]
  );
  // An unreleased song gets its pre-save page from the song (design presave-create a1); a released
  // song gets the creation sheet.
  const openPresave = useOpenPresaveWizardForSong();
  const onCreateLink = useCallback(() => {
    if (isClaimed) {
      setIsCreateOpen(true);
      return;
    }
    openPresave(item.uuid);
  }, [isClaimed, item.uuid, openPresave]);
  const openFiles = useCallback(() => scrollToSongSection('files'), []);
  const editLyrics = useCallback(() => {
    setView('text');
    scrollToSongSection('lyrics');
    window.setTimeout(() => document.getElementById(SONG_LYRICS_INPUT_ID)?.focus({ preventScroll: true }), 400);
  }, []);
  const copyLink = useCallback(() => {
    const link = `${window.location.origin}/app${catalogSongPath(item.uuid, { accountId })}`;
    navigator.clipboard
      ?.writeText(link)
      .then(() => {
        trackEvent('link_copied', { kind: 'track' });
        showSuccess(intl.formatMessage(songPageMessages.menuLinkCopied));
      })
      .catch(console.error);
  }, [accountId, intl, item.uuid, showSuccess]);
  const openRemove = useCallback(() => {
    if (isOwnerOnly) {
      scrollToSongSection('release');
      return;
    }
    deletion.reset();
    setIsRemoveOpen(true);
  }, [deletion, isOwnerOnly]);
  const closeRemove = useCallback(() => setIsRemoveOpen(false), []);

  const leaveToCatalog = useCallback(() => {
    showSuccess(intl.formatMessage(songPageMessages.removed, { title: item.title }));
    // eslint-disable-next-line no-void
    void navigate(`/${SMLRoutes.catalog.join('/')}`, { replace: true });
  }, [intl, item.title, navigate, showSuccess]);

  // The page row left the catalog in the merge: the song page of the row that stays opens.
  const onMerged = useCallback(
    (kept: CatalogItem) => {
      if (kept.uuid === item.uuid) {
        return;
      }
      // eslint-disable-next-line no-void
      void navigate(catalogSongPath(kept.uuid), { replace: true });
    },
    [item.uuid, navigate]
  );

  const confirmRemove = useCallback(() => {
    deleteItem({ accountId, itemUuid: item.uuid })
      .unwrap()
      .then(() => {
        setIsRemoveOpen(false);
        leaveToCatalog();
      })
      .catch((error: unknown) => {
        const statusCode = (error as { statusCode?: number } | null)?.statusCode;
        if (statusCode === 404) {
          setIsRemoveOpen(false);
          leaveToCatalog();
        } else if (readM24Error(error)?.code === 'M24_TEAM_OWNER_ONLY') {
          setIsRemoveOpen(false);
          setIsOwnerOnlyForced(true);
          scrollToSongSection('release');
        }
      });
  }, [accountId, deleteItem, item.uuid, leaveToCatalog]);

  const saveAndLeave = useCallback(() => {
    form
      .save()
      .then((saved) => {
        if (saved) {
          guard.proceed();
        } else {
          guard.cancel();
        }
      })
      .catch(console.error);
  }, [form, guard]);

  let filesBody: React.ReactNode;
  let lyricsBody: React.ReactNode;
  if (songError !== undefined && song === undefined) {
    filesBody = <ErrorView error={songError as never} retry={retrySong} />;
    lyricsBody = null;
  } else if (song === undefined) {
    filesBody = <LoadingView />;
    lyricsBody = <LoadingView />;
  } else {
    filesBody = (
      <SongFilesSection
        accountId={accountId}
        itemUuid={item.uuid}
        song={song}
        editedCount={editedCount}
        readOnly={readOnly}
        isUploading={isUploading}
        onUploadingChange={setIsUploading}
      />
    );
    lyricsBody = (
      <SongLyricsSection
        accountId={accountId}
        itemUuid={item.uuid}
        title={item.title}
        song={song}
        form={form}
        view={lyricsView}
        onViewChange={setView}
        onOpenFiles={openFiles}
        readOnly={readOnly}
      />
    );
  }

  return (
    <div className={styles.page}>
      <SongPageHeader
        item={item}
        song={song}
        kit={kit}
        breadcrumbs={breadcrumbs}
        readOnly={readOnly}
        onStudio={openStudio}
        onReleases={openReleases}
        onCopyLink={copyLink}
        onEditLyrics={editLyrics}
        onRemove={openRemove}
        onMerge={openMerge}
      />
      {showMemberNote && role === 'MEMBER' && !readOnly && workspaceName !== null && (
        <div className={clsx(styles.note, styles.noteInfo)} data-testid="song-page-member-note">
          <FontAwesomeIcon icon={faCircleInfo} />
          <span>{intl.formatMessage(songPageMessages.memberNote, { workspace: workspaceName })}</span>
          <span className={styles.noteAction}>
            <button type="button" className={styles.textButton} onClick={onDismissMemberNote}>
              {intl.formatMessage(songPageMessages.dismiss)}
            </button>
          </span>
        </div>
      )}
      {form.changedElsewhere === 'shown' && (
        <div className={clsx(styles.note, styles.noteInfo)} role="status" data-testid="song-page-changed">
          <FontAwesomeIcon icon={faCircleInfo} />
          <span>{intl.formatMessage(songPageMessages.changedElsewhere, { title: item.title })}</span>
        </div>
      )}
      {form.changedElsewhere === 'whileEditing' && (
        <div className={clsx(styles.note, styles.noteWarn)} role="status" data-testid="song-page-changed-editing">
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <span>{intl.formatMessage(songPageMessages.changedWhileEditing, { title: item.title })}</span>
          <span className={styles.noteAction}>
            <button type="button" className={styles.textButton} onClick={form.discard}>
              {intl.formatMessage(songPageMessages.discardMine)}
            </button>
          </span>
        </div>
      )}
      <SongPageBar accountId={accountId} itemUuid={item.uuid} song={song} />
      <SongPageSectionBlock section="details" title={intl.formatMessage(songPageMessages.sectionDetails)}>
        <SongDetailsSection accountId={accountId} item={item} form={form} readOnly={readOnly} />
      </SongPageSectionBlock>
      <SongPageSectionBlock section="files" title={intl.formatMessage(songPageMessages.sectionFiles)}>
        {filesBody}
      </SongPageSectionBlock>
      <SongPageSectionBlock
        section="lyrics"
        title={intl.formatMessage(songPageMessages.sectionLyrics)}
        aside={song === undefined ? undefined : <SongLyricsViewSwitch view={lyricsView} onChange={setView} />}
      >
        {lyricsBody}
      </SongPageSectionBlock>
      <SongPageSectionBlock section="release" title={intl.formatMessage(songPageMessages.sectionRelease)}>
        <div className={styles.cols}>
          <div className={styles.stack}>
            <SongSmartLinkCard
              accountId={accountId}
              item={item}
              readOnly={readOnly}
              onEdit={openSmartLink}
              onCreate={onCreateLink}
            />
            <ServicesPanel accountId={accountId} item={item} readOnly={readOnly} />
            {!readOnly && (
              <SongRemoveZone
                title={item.title}
                isClaimed={isClaimed}
                isOwnerOnly={isOwnerOnly}
                ownerEmail={ownerEmail}
                onRemove={openRemove}
              />
            )}
          </div>
          <div className={styles.stack}>
            <SongKitCard kit={kit} readOnly={readOnly} onReleases={openReleases} onStudio={openStudio} />
            {song !== undefined && (
              <Panel>
                <PanelBody className={styles.cardBody}>
                  <SongMadeList
                    title={item.title}
                    made={song.made}
                    onOpenSong={openReleases}
                    onOpenReels={openReels}
                    onOpenCalendar={openCalendar}
                  />
                </PanelBody>
              </Panel>
            )}
          </div>
        </div>
      </SongPageSectionBlock>
      {!readOnly && <SongSaveBar form={form} />}
      <SongRemoveDialog
        isOpen={isRemoveOpen}
        title={item.title}
        isClaimed={isClaimed}
        isBusy={deletion.isLoading}
        error={deletion.error === undefined ? undefined : errorAlertMessage(intl, deletion.error)}
        onConfirm={confirmRemove}
        onCancel={closeRemove}
      />
      <SongLeaveDialog
        isOpen={guard.isBlocked}
        title={item.title}
        fields={form.isDirty ? changedFieldNames(intl, form.changed) : ''}
        isUploading={isUploading}
        isSaving={form.status === 'saving'}
        onLeave={guard.proceed}
        onStay={guard.cancel}
        onSaveAndLeave={saveAndLeave}
      />
      <MergeSongsModal
        accountId={accountId}
        source={isMergeOpen ? item : undefined}
        closeModal={closeMerge}
        onMerged={onMerged}
      />
      <SMLCreationSheet
        accountId={accountId}
        isOpen={isCreateOpen}
        closeModal={() => setIsCreateOpen(false)}
        initialCatalogTrackMaid={catalogCreationSource(item).catalogTrackMaid}
        initialCatalogItemUuid={catalogCreationSource(item).catalogItemUuid}
      />
    </div>
  );
};

export const SongPage = TypedMemo(SongPageComponent);
